import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import { getChallenges, getPastChallenges } from './thunks'
import { createNewChallenge, updateChallenge, deleteChallenge, duplicateChallenge } from 'store/challenge/thunks'

const challengesState: Challenges = {
  challenges: [],
  status: 'idle',
  error: null,
  offset: 0,
  loadingMoreStatus: 'idle',
  shouldRequestMore: true,
}

const initialState: ChallengesContainer = {
  current: challengesState,
  past: challengesState,
}

const slice = createSlice({
  name: 'challenges',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getChallenges.pending, (state) => {
        state.current.status = 'loading'
        state.current.error = null
      })
      .addCase(getChallenges.rejected, (state, action) => {
        state.current.status = 'failed'
        state.current.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getChallenges.fulfilled, (state, action) => {
        state.current.status = 'succeeded'
        if (action.payload.data) {
          state.current.challenges = action.payload.data
          state.current.shouldRequestMore = false
        }
      })

    builder
      .addCase(getPastChallenges.pending, (state, action) => {
        const isFirstLoad = action.meta.arg.offset === 0
        if (isFirstLoad) {
          state.past.status = 'loading'
        } else {
          state.past.loadingMoreStatus = 'loading'
        }
        state.past.error = null
      })
      .addCase(getPastChallenges.rejected, (state, action) => {
        const isFirstLoad = action.meta.arg.offset === 0
        if (isFirstLoad) {
          state.past.status = 'failed'
        } else {
          state.past.loadingMoreStatus = 'failed'
        }
        state.past.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getPastChallenges.fulfilled, (state, action) => {
        state.past.status = 'succeeded'
        state.past.loadingMoreStatus = 'succeeded'
        state.past.challenges = state.past.challenges.concat(action.payload)
        const offset = action.meta.arg.offset
        const limit = action.meta.arg.limit
        state.past.offset = offset + limit
        state.past.shouldRequestMore = action.payload.length === limit
      })

    builder.addCase(createNewChallenge.fulfilled, (state, action) => {
      if (!action.payload.data) {
        return
      }
      state.current.challenges = state.current.challenges
        .concat(action.payload.data)
        .sort((a, b) => (Date.parse(a.schedule?.endAt ?? '') < Date.parse(b.schedule?.endAt ?? '') ? 1 : -1))
    })

    builder.addCase(updateChallenge.fulfilled, (state, action) => {
      const updatedId = action.payload.data?.challengeId
      const currentIndex = state.current.challenges
        .map((challenge: Challenge) => challenge.challengeId ?? '')
        .indexOf(updatedId ?? '')
      if (currentIndex >= 0 && action.payload.data) {
        state.current.challenges[currentIndex] = action.payload.data
      } else if (action.payload.data) {
        const pastIndex = state.past.challenges
          .map((challenge: Challenge) => challenge.challengeId ?? '')
          .indexOf(updatedId ?? '')
        state.past.challenges[pastIndex] = action.payload.data
      }
    })

    builder.addCase(deleteChallenge.fulfilled, (state, action) => {
      const deletedId = action.meta.arg
      const currentIndex = state.current.challenges
        .map((challenge: Challenge) => challenge.challengeId)
        .indexOf(deletedId)
      if (currentIndex >= 0) {
        state.current.challenges.splice(currentIndex, 1)
      } else {
        const pastIndex = state.past.challenges.map((challenge: Challenge) => challenge.challengeId).indexOf(deletedId)
        state.past.challenges.splice(pastIndex, 1)
      }
    })

    builder.addCase(duplicateChallenge.fulfilled, (state, action) => {
      const type = Date.parse(action.payload.schedule?.endAt ?? '') > new Date().getTime() ? 'current' : 'past'
      const updatedChallenges = state[type].challenges.concat(action.payload)
      state[type].challenges = updatedChallenges.sort((a, b) =>
        Date.parse(a.schedule?.endAt ?? '') < Date.parse(b.schedule?.endAt ?? '') ? 1 : -1,
      )
    })
  },
})

export const selectChallenges = (state: RootState): Challenges => state.challenges.current

export default slice.reducer
